import { Observable, of } from "rxjs";
import { mergeMap, map, catchError } from "rxjs/operators";
import { ofType, Epic } from "redux-observable";
import * as _ from "lodash";

import { Action, Epoch } from "@ctra/utils";

import { makeAzureApiURL } from "../../utils/ajax";
import types from "./types";
import actions from "./actions";
import { GamePlanEntity } from "./typings";

type FetchGamePlansResponse = Array<Record<string, unknown>>;
type CreateGamePlanResponse = Record<string, unknown>;
type UpdateGamePlanResponse = Record<string, unknown>;
type CreateTaskListResponse = Array<Record<string, unknown>>;
type UpdateTaskListResponse = Array<Record<string, unknown>>;

/**
 * Pre-process game plan by removing invalid tasks with NULL as id
 * @param taskList
 * @param id
 * @param gamePlan
 */
const preprocessGamePlan = ({ taskList, id, ...gamePlan }: Record<string, unknown>) => ({
  ...gamePlan,
  id,
  taskList: _.filter(taskList as Array<unknown>, _.negate(_.matchesProperty("id", null)))
});

/**
 * Fetch game plans for the server
 * @param action$
 * @param state$
 * @param Request
 */
const fetchGamePlans: Epic = (action$, state$, { Request }) =>
  action$.pipe(
    ofType(types.FETCH_GAME_PLAN_LIST.pending),
    mergeMap<ReturnType<typeof actions.fetchGamePlans.start>, Observable<Promise<unknown>>>(
      ({ payload: { farmID } }) => {
        /**
         * Tell whether all farms need to be fetched.
         * As it is a unique value, it canot be undefined.
         */
        const allFarms = farmID === -1;

        const url = makeAzureApiURL("gameplans", `/gameplans${allFarms ? "" : "/farms"}`)();

        return Request.GET(url, {
          body: allFarms ? {} : { farmId: farmID }
        }).pipe(
          map<{ response: FetchGamePlansResponse }, Action>(({ response }) =>
            actions.fetchGamePlans.fulfill(farmID, _.map(response, preprocessGamePlan))
          ),
          catchError<unknown, Observable<Action>>((error) => of(actions.fetchGamePlans.reject(farmID, error)))
        );
      }
    )
  );

/**
 * Create a new game plan
 * @param action$
 * @param state$
 * @param Request
 */
const createGamePlan: Epic = (action$, state$, { Request }) =>
  action$.pipe(
    ofType(types.CREATE_GAME_PLAN.pending),
    mergeMap<ReturnType<typeof actions.createGamePlan.start>, Observable<Promise<unknown>>>(({ payload }) =>
      Request.POST(makeAzureApiURL("gameplans", "/gameplans")(), {
        body: payload
      }).pipe(
        mergeMap<{ response: CreateGamePlanResponse }, Observable<Action>>(({ response }) => {
          /**
           * Filter out invalid tasks
           */
          const preprocessed = preprocessGamePlan(response);

          /**
           * Tell if the tasks have been created properly
           */
          const mayCreateTaskList =
            preprocessed.taskList.length !== (response.taskList as Array<unknown>).length;

          /**
           * Either fulfill the createGamePlan including the tasks or
           * dispatch a createTaskList action
           */
          const actionList = _.compact([
            mayCreateTaskList
              ? actions.createTaskList.start(preprocessed.id as string, payload.taskList)
              : null,
            actions.createGamePlan.fulfill(preprocessed)
          ]);

          return of(...actionList);
        }),
        catchError<unknown, Observable<Action>>((error) => of(actions.createGamePlan.reject(error)))
      )
    )
  );

/**
 * Update an existing game plan
 * @param action$
 * @param state$
 * @param Request
 */
const updateGamePlan: Epic = (action$, state$, { Request }) =>
  action$.pipe(
    ofType(types.UPDATE_GAME_PLAN.pending),
    mergeMap<ReturnType<typeof actions.updateGamePlan.start>, Observable<Promise<unknown>>>(
      ({ payload: { id, taskList, farmID, ...rest } }) =>
        Request.PUT(makeAzureApiURL("gameplans", "/gameplans/<%= id %>")({ id }), {
          body: {
            id,
            taskList,
            scopeID: farmID?.toString(),
            ...rest
          }
        }).pipe(
          mergeMap<{ response: UpdateGamePlanResponse }, Observable<Action>>(({ response }) => {
            /**
             * Fulfill the updateGamePlan and dispatch an updateTaskList action
             */
            const actionList = _.compact([
              actions.updateTaskList.start(id as string, taskList),
              actions.updateGamePlan.fulfill(response)
            ]);

            return of(...actionList);
          }),
          catchError<unknown, Observable<Action>>((error) =>
            of(actions.updateGamePlan.reject(id as GamePlanEntity["id"], error))
          )
        )
    )
  );

/**
 * Create a new game plan
 * @param action$
 * @param state$
 * @param Request
 */
const deleteGamePlan: Epic = (action$, state$, { Request }) =>
  action$.pipe(
    ofType(types.DELETE_GAME_PLAN.pending),
    mergeMap<ReturnType<typeof actions.deleteGamePlan.start>, Observable<Promise<unknown>>>(
      ({ payload: { id } }) =>
        Request.DELETE(makeAzureApiURL("gameplans", "/gameplans/<%= id %>")({ id })).pipe(
          map<unknown, Action>(() => actions.deleteGamePlan.fulfill(id)),
          catchError<unknown, Observable<Action>>((error) => of(actions.deleteGamePlan.reject(id, error)))
        )
    )
  );

/**
 * Create a task list for the given game plan
 * @param action$
 * @param state$
 * @param Request
 */
const createTaskList: Epic = (action$, state$, { Request }) =>
  action$.pipe(
    ofType(types.CREATE_TODO_LIST.pending),
    mergeMap<ReturnType<typeof actions.createTaskList.start>, Observable<Promise<unknown>>>(
      ({ payload: { gamePlanID, taskList } }) =>
        Request.POST(makeAzureApiURL("gameplans", "/gameplans/<%= gamePlanID %>/addtasks")({ gamePlanID }), {
          body: taskList
        }).pipe(
          map<{ response: CreateTaskListResponse }, Action>(({ response }) =>
            actions.createTaskList.fulfill(gamePlanID, response)
          ),
          catchError<unknown, Observable<Action>>((error) =>
            of(actions.createTaskList.reject(gamePlanID, error))
          )
        )
    )
  );

/**
 * Update the task list for the given game plan
 * @param action$
 * @param state$
 * @param Request
 */
const updateTaskList: Epic = (action$, state$, { Request }) =>
  action$.pipe(
    ofType(types.UPDATE_TODO_LIST.pending),
    mergeMap<ReturnType<typeof actions.updateTaskList.start>, Observable<Promise<unknown>>>(
      ({ payload: { gamePlanID, taskList } }) =>
        Request.PUT(makeAzureApiURL("gameplans", "/gameplans/<%= gamePlanID %>/tasklist")({ gamePlanID }), {
          body: taskList
        }).pipe(
          map<{ response: UpdateTaskListResponse }, Action>(({ response }) =>
            actions.updateTaskList.fulfill(gamePlanID, response)
          ),
          catchError<unknown, Observable<Action>>((error) =>
            of(actions.updateTaskList.reject(gamePlanID, error))
          )
        )
    )
  );

/**
 * Update a single task
 * @param action$
 * @param state$
 * @param Request
 */
const updateTask: Epic = (action$, state$, { Request }) =>
  action$.pipe(
    ofType(types.UPDATE_TASK.pending),
    mergeMap<ReturnType<typeof actions.updateTask.start>, Observable<Promise<unknown>>>(
      ({ payload: { gamePlanID, id, modifiedEpoch, ...task } }) =>
        Request.PUT(
          makeAzureApiURL("gameplans", "/gameplans/<%= gamePlanID %>/task/<%= id %>")({ gamePlanID, id }),
          {
            body: {
              id: id,
              modifiedEpoch: Epoch.asLong(modifiedEpoch),
              ...task
            }
          }
        ).pipe(
          map(() => actions.updateTask.fulfill({ id, modifiedEpoch, ...task })),
          catchError<unknown, Observable<Action>>((error) => of(actions.updateTask.reject(id, error)))
        )
    )
  );

export default {
  fetchGamePlans,
  createGamePlan,
  updateGamePlan,
  deleteGamePlan,
  createTaskList,
  updateTaskList,
  updateTask
};
